/**
 * Shared Settings primitives — small presentational pieces used by both the
 * Settings screen (brand-scoped config) and the Operator screen (one-per-
 * server controls). Extracted out of Settings.tsx so neither screen has to
 * duplicate them. No behavior change from the originals.
 */
import { Check } from "lucide-react";
import { cardStyle } from "../styles";

/**
 * Standard sub-section card used by Brand/Voice/Models/Keys/Cache/Limits.
 * Cookies + Scheduling render their own outer card shell because they
 * already had it before the redesign — preserving that keeps their real
 * API wiring byte-stable.
 */
export function SettingCard({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={{ ...cardStyle, padding: 24, marginBottom: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: "var(--ql-fg)" }}>
        {title}
      </div>
      {sub && (
        <div
          style={{
            fontSize: 12,
            color: "var(--ql-fg-muted)",
            marginBottom: 16,
            lineHeight: 1.55,
          }}
        >
          {sub}
        </div>
      )}
      {children}
    </div>
  );
}

export function LoadingCard(): React.JSX.Element {
  return (
    <div
      style={{
        ...cardStyle,
        padding: 32,
        color: "var(--ql-fg-muted)",
        textAlign: "center",
        fontSize: 13,
      }}
    >
      Loading…
    </div>
  );
}

export const v2Label: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--ql-fg-muted)",
  marginBottom: 8,
  textTransform: "uppercase",
  letterSpacing: ".08em",
};

export const v2Input: React.CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.10)",
  background: "rgba(17,24,39,.5)",
  color: "var(--ql-fg)",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

export const v2Mono: React.CSSProperties = {
  ...v2Input,
  fontFamily: "var(--ql-font-mono)",
};

/** The yellow pill toggle used across Settings (autoScan pattern). */
export function PillToggle({
  on,
  disabled,
  onChange,
}: {
  on: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}): React.JSX.Element {
  return (
    <label style={{ cursor: disabled ? "wait" : "pointer", flexShrink: 0 }}>
      <div
        style={{
          width: 40, height: 22, borderRadius: 999, position: "relative",
          background: on ? "linear-gradient(90deg,#FFD500,#FFA500)" : "rgba(255,255,255,.10)",
          transition: "background .2s var(--ql-ease)",
          opacity: disabled ? 0.7 : 1,
        }}
      >
        <div
          style={{
            position: "absolute", top: 2, left: on ? 20 : 2, width: 18, height: 18,
            borderRadius: 999, background: "#fff",
            transition: "left .2s var(--ql-ease)",
            boxShadow: "0 2px 4px rgba(0,0,0,.3)",
          }}
        />
      </div>
      <input
        type="checkbox"
        checked={on}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ display: "none" }}
      />
    </label>
  );
}

export function PillStatus({
  ok,
  label,
  badLabel,
}: {
  ok: boolean;
  label: string;
  badLabel: string;
}): React.JSX.Element {
  return (
    <span
      style={{
        fontSize: 10,
        padding: "4px 10px",
        borderRadius: 999,
        background: ok ? "rgba(74,222,128,.14)" : "rgba(248,113,113,.14)",
        color: ok ? "#4ADE80" : "#F87171",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {ok && <Check size={10} strokeWidth={3} />}
      {ok ? label : badLabel}
    </span>
  );
}

export function KV({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>
      {label}: <span style={{ color: "var(--ql-fg)", fontWeight: 600 }}>{value}</span>
    </div>
  );
}

// ---------- formatting helpers --------------------------------------------
// Shared by CacheSection (Operator) and PoolCard/ProxyCard/CookiesCard
// (Operator) plus SchedulingCard (Settings) — kept here once rather than
// duplicated per screen.

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function formatRelativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return `${Math.round(diff / 86_400_000)}d ago`;
}
